import { useState, useCallback } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Alert,
} from '@mui/material'
import { ImageUploadZone } from '@components/common/ImageUploadZone'
import { ImageCropper } from '@components/common/ImageCropper'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'
import { useUploadAdventureFile } from '../../../hooks/useAdventureFiles'
import { adventureFilesApi } from '@api/endpoints/adventureFiles'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import { useAdventureCollaboration } from '@contexts/AdventureCollaborationContext'
import { ImageFileSelectorDialog } from '../media/ImageFileSelectorDialog'
import { WithFieldChangeIndicator } from '../FieldChangeIndicator'
import type { AdventureMediaFileDto } from '@api/types/adventureFiles'

// Image CDN base URL
const IMAGE_CDN_BASE = 'https://images.hadv.quest/'

// Normalize image URL to ensure it has the correct base
function normalizeImageUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined
  // If it's already a full URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  // Otherwise, prefix with the CDN base URL
  return `${IMAGE_CDN_BASE}${url.startsWith('/') ? url.slice(1) : url}`
}

export function ImagesPanel() {
  const adventureId = useAdventureEditorStore((state) => state.adventureId)
  const adventure = useAdventureEditorStore((state) => state.adventure)
  const updateAdventure = useAdventureEditorStore((state) => state.updateAdventure)

  // Published permissions - only admins/moderators can edit images on published adventures
  const { isPublished, canEditImages } = usePublishedPermissions()

  // Collaboration - broadcast image changes to other editors
  const { isConnected, sendAdventureFieldUpdate } = useAdventureCollaboration()

  // File manager upload hook
  const uploadFile = useUploadAdventureFile(adventureId)

  const [thumbnailUploading, setThumbnailUploading] = useState(false)
  const [coverUploading, setCoverUploading] = useState(false)
  const [thumbnailError, setThumbnailError] = useState<string | null>(null)
  const [coverError, setCoverError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // File selector state
  const [thumbnailSelectorOpen, setThumbnailSelectorOpen] = useState(false)
  const [coverSelectorOpen, setCoverSelectorOpen] = useState(false)

  // Cropper state for selected files
  const [cropperOpen, setCropperOpen] = useState(false)
  const [fileToCrop, setFileToCrop] = useState<File | null>(null)
  const [cropperAspectRatio, setCropperAspectRatio] = useState<number>(4 / 3)
  const [cropperTitle, setCropperTitle] = useState<string>('Crop Image')
  const [pendingImageType, setPendingImageType] = useState<'thumbnail' | 'cover' | null>(null)

  const handleThumbnailUpload = async (file: File) => {
    if (!adventureId) {
      setThumbnailError('Please save the adventure first before uploading images')
      return
    }

    setThumbnailUploading(true)
    setThumbnailError(null)
    setSuccessMessage(null)

    try {
      const result = await uploadFile.mutateAsync({
        file,
        options: {
          displayName: 'Adventure Thumbnail',
          description: 'Thumbnail image for adventure cards and search results',
          tags: ['thumbnail', 'adventure-image'],
        },
      })

      const url = result.url || result.file?.fileUrl
      const fileId = result.file?.id

      if (fileId) {
        // Call API to set thumbnail
        await adventureFilesApi.setAdventureImage(adventureId, fileId, 'thumbnail')
      }

      if (url && updateAdventure) {
        updateAdventure({ thumbnailImageUrl: url, thumbnailImageId: fileId })
        // Broadcast to other editors
        if (isConnected) {
          sendAdventureFieldUpdate('thumbnailImageUrl', url)
        }
      }

      setSuccessMessage('Thumbnail uploaded successfully')
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to upload thumbnail'
      setThumbnailError(message)
    } finally {
      setThumbnailUploading(false)
    }
  }

  const handleCoverUpload = async (file: File) => {
    if (!adventureId) {
      setCoverError('Please save the adventure first before uploading images')
      return
    }

    setCoverUploading(true)
    setCoverError(null)
    setSuccessMessage(null)

    try {
      const result = await uploadFile.mutateAsync({
        file,
        options: {
          displayName: 'Adventure Cover Photo',
          description: 'Cover photo for adventure detail page banner',
          tags: ['cover', 'adventure-image'],
        },
      })

      const url = result.url || result.file?.fileUrl
      const fileId = result.file?.id

      if (fileId) {
        // Call API to set cover
        await adventureFilesApi.setAdventureImage(adventureId, fileId, 'cover')
      }

      if (url && updateAdventure) {
        updateAdventure({ coverImageUrl: url, coverImageId: fileId })
        // Broadcast to other editors
        if (isConnected) {
          sendAdventureFieldUpdate('coverImageUrl', url)
        }
      }

      setSuccessMessage('Cover photo uploaded successfully')
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to upload cover photo'
      setCoverError(message)
    } finally {
      setCoverUploading(false)
    }
  }

  const handleThumbnailClear = async () => {
    setThumbnailError(null)

    if (adventureId) {
      try {
        // Call API to clear thumbnail
        await adventureFilesApi.setAdventureImage(adventureId, null, 'thumbnail')
      } catch (error: any) {
        console.error('Failed to clear thumbnail:', error)
        setThumbnailError('Failed to clear thumbnail')
        return
      }
    }

    if (updateAdventure) {
      updateAdventure({ thumbnailImageUrl: undefined, thumbnailImageId: undefined })
      // Broadcast to other editors
      if (isConnected) {
        sendAdventureFieldUpdate('thumbnailImageUrl', null)
      }
    }
  }

  const handleCoverClear = async () => {
    setCoverError(null)

    if (adventureId) {
      try {
        // Call API to clear cover
        await adventureFilesApi.setAdventureImage(adventureId, null, 'cover')
      } catch (error: any) {
        console.error('Failed to clear cover:', error)
        setCoverError('Failed to clear cover photo')
        return
      }
    }

    if (updateAdventure) {
      updateAdventure({ coverImageUrl: undefined, coverImageId: undefined })
      // Broadcast to other editors
      if (isConnected) {
        sendAdventureFieldUpdate('coverImageUrl', null)
      }
    }
  }

  // Download image via backend proxy and convert to File for cropping
  const fetchImageAsFile = useCallback(async (fileId: string, fileName: string): Promise<File> => {
    if (!adventureId) {
      throw new Error('Adventure ID is required')
    }
    const blob = await adventureFilesApi.downloadFile(adventureId, fileId)
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' })
  }, [adventureId])

  // Check if image needs cropping based on aspect ratio
  const checkAspectRatio = (
    width: number | null,
    height: number | null,
    targetRatio: number,
    tolerance: number = 0.1
  ): boolean => {
    if (!width || !height) return false
    const currentRatio = width / height
    return Math.abs(currentRatio - targetRatio) <= tolerance
  }

  // Handle file selection from file manager for thumbnail
  const handleThumbnailFileSelect = async (file: AdventureMediaFileDto) => {
    if (!adventureId) return

    setThumbnailError(null)
    setSuccessMessage(null)

    const targetRatio = 4 / 3
    const matchesRatio = checkAspectRatio(file.width, file.height, targetRatio)

    if (matchesRatio) {
      // Aspect ratio matches, use directly
      try {
        // Call API to set thumbnail
        await adventureFilesApi.setAdventureImage(adventureId, file.id, 'thumbnail')

        if (updateAdventure) {
          updateAdventure({ thumbnailImageUrl: file.fileUrl, thumbnailImageId: file.id })
          // Broadcast to other editors
          if (isConnected) {
            sendAdventureFieldUpdate('thumbnailImageUrl', file.fileUrl)
          }
        }
        setSuccessMessage('Thumbnail selected successfully')
      } catch (error: any) {
        console.error('Failed to set thumbnail:', error)
        setThumbnailError('Failed to set thumbnail')
      }
    } else {
      // Need to crop - download via proxy and open cropper
      try {
        setThumbnailUploading(true)
        const imageFile = await fetchImageAsFile(file.id, file.fileName)
        setFileToCrop(imageFile)
        setCropperAspectRatio(targetRatio)
        setCropperTitle('Crop Thumbnail (4:3)')
        setPendingImageType('thumbnail')
        setCropperOpen(true)
      } catch (error) {
        console.error('Could not load image for cropping:', error)
        setThumbnailError('Could not load image for cropping. Please try uploading locally instead.')
      } finally {
        setThumbnailUploading(false)
      }
    }
  }

  // Handle file selection from file manager for cover
  const handleCoverFileSelect = async (file: AdventureMediaFileDto) => {
    if (!adventureId) return

    setCoverError(null)
    setSuccessMessage(null)

    const targetRatio = 2 / 1
    const matchesRatio = checkAspectRatio(file.width, file.height, targetRatio)

    if (matchesRatio) {
      // Aspect ratio matches, use directly
      try {
        // Call API to set cover
        await adventureFilesApi.setAdventureImage(adventureId, file.id, 'cover')

        if (updateAdventure) {
          updateAdventure({ coverImageUrl: file.fileUrl, coverImageId: file.id })
          // Broadcast to other editors
          if (isConnected) {
            sendAdventureFieldUpdate('coverImageUrl', file.fileUrl)
          }
        }
        setSuccessMessage('Cover photo selected successfully')
      } catch (error: any) {
        console.error('Failed to set cover:', error)
        setCoverError('Failed to set cover photo')
      }
    } else {
      // Need to crop - download via proxy and open cropper
      try {
        setCoverUploading(true)
        const imageFile = await fetchImageAsFile(file.id, file.fileName)
        setFileToCrop(imageFile)
        setCropperAspectRatio(targetRatio)
        setCropperTitle('Crop Cover Photo (2:1)')
        setPendingImageType('cover')
        setCropperOpen(true)
      } catch (error) {
        console.error('Could not load image for cropping:', error)
        setCoverError('Could not load image for cropping. Please try uploading locally instead.')
      } finally {
        setCoverUploading(false)
      }
    }
  }

  // Handle crop completion - upload cropped image
  const handleCropComplete = async (croppedFile: File) => {
    setCropperOpen(false)
    setFileToCrop(null)

    if (!adventureId || !pendingImageType) return

    const setUploading = pendingImageType === 'thumbnail' ? setThumbnailUploading : setCoverUploading
    const setError = pendingImageType === 'thumbnail' ? setThumbnailError : setCoverError

    setUploading(true)

    try {
      const result = await uploadFile.mutateAsync({
        file: croppedFile,
        options: {
          displayName: pendingImageType === 'thumbnail' ? 'Adventure Thumbnail' : 'Adventure Cover Photo',
          description: pendingImageType === 'thumbnail'
            ? 'Thumbnail image for adventure cards and search results'
            : 'Cover photo for adventure detail page banner',
          tags: [pendingImageType, 'adventure-image'],
        },
      })

      // Extract URL and ID from response
      const url = result.url || result.file?.fileUrl
      const fileId = result.file?.id

      if (!url || !fileId) {
        setError('Upload succeeded but no URL or file ID was returned')
        return
      }

      // Call API to set thumbnail/cover
      await adventureFilesApi.setAdventureImage(adventureId, fileId, pendingImageType)

      if (updateAdventure) {
        if (pendingImageType === 'thumbnail') {
          updateAdventure({ thumbnailImageUrl: url, thumbnailImageId: fileId })
          // Broadcast to other editors
          if (isConnected) {
            sendAdventureFieldUpdate('thumbnailImageUrl', url)
          }
        } else {
          updateAdventure({ coverImageUrl: url, coverImageId: fileId })
          // Broadcast to other editors
          if (isConnected) {
            sendAdventureFieldUpdate('coverImageUrl', url)
          }
        }
      }

      setSuccessMessage(
        pendingImageType === 'thumbnail'
          ? 'Thumbnail cropped and uploaded successfully'
          : 'Cover photo cropped and uploaded successfully'
      )
    } catch (error: any) {
      console.error('Upload error:', error)
      const message = error.response?.data?.message || error.message || 'Failed to upload cropped image'
      setError(message)
    } finally {
      setUploading(false)
      setPendingImageType(null)
    }
  }

  const handleCropperClose = () => {
    setCropperOpen(false)
    setFileToCrop(null)
    setPendingImageType(null)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* No Adventure ID Warning */}
      {!adventureId && (
        <Alert severity="warning">
          Please save the adventure first before uploading images.
        </Alert>
      )}

      {/* Published Read-Only Warning */}
      {isPublished && !canEditImages && (
        <Alert severity="info">
          Images are read-only because this adventure is published. Only system moderators can modify images.
        </Alert>
      )}

      {/* Thumbnail Upload */}
      <Card>
        <CardHeader
          title="Thumbnail Image"
          subheader="This image appears in adventure lists and search results"
        />
        <CardContent>
          <WithFieldChangeIndicator field="thumbnailImageUrl" offset={{ top: 8, right: 8 }}>
            <ImageUploadZone
              value={normalizeImageUrl(adventure?.thumbnailImageUrl)}
              onFileSelect={handleThumbnailUpload}
              onClear={handleThumbnailClear}
              label="Upload Thumbnail"
              description="Recommended size: 400x300 pixels"
              aspectRatio="4:3"
              maxSizeMB={5}
              isUploading={thumbnailUploading}
              error={thumbnailError || undefined}
              disabled={!adventureId || !canEditImages}
              onSelectFromFiles={adventureId && canEditImages ? () => setThumbnailSelectorOpen(true) : undefined}
            />
          </WithFieldChangeIndicator>
        </CardContent>
      </Card>

      {/* Cover Photo Upload */}
      <Card>
        <CardHeader
          title="Cover Photo"
          subheader="This image appears at the top of the adventure detail page"
        />
        <CardContent>
          <WithFieldChangeIndicator field="coverImageUrl" offset={{ top: 8, right: 8 }}>
            <ImageUploadZone
              value={normalizeImageUrl(adventure?.coverImageUrl)}
              onFileSelect={handleCoverUpload}
              onClear={handleCoverClear}
              label="Upload Cover Photo"
              description="Recommended size: 1200x600 pixels"
              aspectRatio="2:1"
              maxSizeMB={10}
              isUploading={coverUploading}
              error={coverError || undefined}
              disabled={!adventureId || !canEditImages}
              onSelectFromFiles={adventureId && canEditImages ? () => setCoverSelectorOpen(true) : undefined}
            />
          </WithFieldChangeIndicator>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader title="Image Guidelines" />
        <CardContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Thumbnail (4:3):</strong> Used for adventure cards and search results.
            Keep important content centered as images may be cropped on different screen sizes.
            The interactive cropper will help you select the best area.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Cover Photo (2:1):</strong> Displayed as a banner on the adventure detail page.
            Use high-quality, wide images that represent your adventure well.
            The interactive cropper ensures your image fits the banner format perfectly.
          </Typography>
        </CardContent>
      </Card>

      {/* File Selector Dialogs */}
      <ImageFileSelectorDialog
        open={thumbnailSelectorOpen}
        onClose={() => setThumbnailSelectorOpen(false)}
        onSelect={handleThumbnailFileSelect}
        adventureId={adventureId}
        title="Select Thumbnail Image"
        description="Choose an existing image from your adventure files. If the aspect ratio doesn't match 4:3, you'll be able to crop it."
      />

      <ImageFileSelectorDialog
        open={coverSelectorOpen}
        onClose={() => setCoverSelectorOpen(false)}
        onSelect={handleCoverFileSelect}
        adventureId={adventureId}
        title="Select Cover Photo"
        description="Choose an existing image from your adventure files. If the aspect ratio doesn't match 2:1, you'll be able to crop it."
      />

      {/* Cropper for selected files */}
      <ImageCropper
        open={cropperOpen}
        imageFile={fileToCrop}
        onCropComplete={handleCropComplete}
        onClose={handleCropperClose}
        aspectRatio={cropperAspectRatio}
        title={cropperTitle}
      />
    </Box>
  )
}
